import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { getMovieById, getRecommendations } from "@/utils/movie";

export default function Detail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: movie ? `${movie.title} - 黑桃视频影视详情` : "影视内容不存在 - 黑桃视频",
    description: movie
      ? movie.summary
      : "该影视内容不存在或已下架，返回黑桃视频首页浏览更多影视资讯与推荐。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},${movie.category},影视详情,黑桃视频`
      : "黑桃视频,影视详情",
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  if (!movie) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center text-foreground-400">
            <i className="ri-emotion-sad-line text-[48px]" aria-hidden="true"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-600 text-foreground-950">影视内容不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">你可能访问了一个已失效的影视详情页面。</p>
          <Link
            to="/"
            className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const favors = isFavorite(movie.id);
  const recommendations = getRecommendations(movie, 4);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    toggleFavorite(movie.id);
    showToast(favors ? "已取消收藏" : "已加入收藏");
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-6 md:px-8 md:pt-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-500">首页</Link>
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-s-line text-[14px]" aria-hidden="true"></i>
          </span>
          <Link to={`/category?category=${encodeURIComponent(movie.category)}`} className="transition-colors hover:text-primary-500">
            {movie.category}
          </Link>
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-s-line text-[14px]" aria-hidden="true"></i>
          </span>
          <span className="text-foreground-800">{movie.title}</span>
        </nav>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-500"
        >
          <i className="ri-arrow-left-line text-[16px]" aria-hidden="true"></i>
          返回上一页
        </button>

        <div className="mt-5 flex flex-col gap-6 md:flex-row md:gap-8">
          <div className="w-full md:w-[300px] md:shrink-0">
            <div className="relative mx-auto aspect-[2/3] w-full max-w-[240px] overflow-hidden rounded-xl border border-background-200 bg-background-200 md:max-w-none">
              <img
                src={movie.cover}
                alt={`${movie.title} 高清封面`}
                title={`${movie.title} - 黑桃视频影视详情`}
                className="h-full w-full object-top"
              />
            </div>
            <div className="mt-4 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-500 transition-colors ${
                  favors
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-500"
                }`}
              >
                <i className={favors ? "ri-heart-fill text-[18px]" : "ri-heart-line text-[18px]"} aria-hidden="true"></i>
                {favors ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-500 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-500"
              >
                <i className="ri-share-line text-[18px]" aria-hidden="true"></i>
                分享链接
              </button>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-2xl font-700 tracking-wide text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {movie.tags.map((t) => (
                <span key={t} className="rounded-full bg-primary-500/15 px-2.5 py-1 text-xs text-primary-400">
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5 text-accent-500">
                <i className="ri-star-fill text-[16px]" aria-hidden="true"></i>
                <strong className="font-600">{movie.rating.toFixed(1)}</strong>
                <span className="text-foreground-500">分</span>
              </span>
              <span>类型：{movie.genres.join(" / ")}</span>
              <span>地区：{movie.region}</span>
              <span>年份：{movie.year}</span>
              <span>{movie.episodes}</span>
            </div>

            <dl className="mt-5 space-y-2.5 rounded-lg border border-background-200 bg-background-100 p-4 text-sm">
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                <dd className="text-foreground-900">{movie.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                <dd className="text-foreground-900">{movie.actors.join("、")}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-foreground-500">更新时间</dt>
                <dd className="text-foreground-900">{movie.updateDate}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-foreground-500">所属分类</dt>
                <dd>
                  <Link
                    to={`/category?category=${encodeURIComponent(movie.category)}&sub=${encodeURIComponent(movie.subCategory)}`}
                    className="text-primary-500 transition-colors hover:text-primary-400"
                  >
                    {movie.category} · {movie.subCategory}
                  </Link>
                </dd>
              </div>
            </dl>

            <div className="mt-6">
              <h4 className="font-heading text-lg font-600 text-foreground-950">剧情简介</h4>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.summary}</p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">{movie.story}</p>
            </div>
          </div>
        </div>

        <section className="mt-14">
          <SectionHeading
            icon="ri-magic-line"
            title="相关推荐"
            subtitle="依据类型相近原则为你推荐"
            moreTo={`/category?category=${encodeURIComponent(movie.category)}`}
          />
          {recommendations.length === 0 ? (
            <p className="rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-8 text-center text-sm text-foreground-500">
              暂无相关推荐
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4">
              {recommendations.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          )}
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 shadow-none">
          {toast}
        </div>
      )}
    </Layout>
  );
}